import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';

interface StatRow {
  _id: string;
  modalidad: 'classic' | 'nohit';
  dificultad: 'normal' | 'dificil';
  puntos: number;
  aciertos: number;
  totalPreguntas?: number;
  fechaPartida: string;
}

interface UserStatsResponse {
  nickname: string;
  porcentajeAciertoMedio: number;
  totalPartidas: number;
  totalPuntos: number;
  totalAciertos: number;
  totalPreguntas: number;
  mejorRachaNoHit: number;
  rachaActualNoHit: number;
  rachaDiasActual: number;
  mejorRachaDias: number;
  participoUltimas24h: boolean;
  ultimaPartidaAt: string | null;
  activityCalendar: Array<{
    date: string;
    partidas: number;
    activo: boolean;
  }>;
  dificultadStats: Array<{
    dificultad: 'normal' | 'dificil';
    partidas: number;
    promedioPuntos: number;
    porcentajeAcierto: number;
  }>;
  weeklyEvolution: Array<{
    label: string;
    partidas: number;
    puntosTotales: number;
    porcentajeAcierto: number;
  }>;
  fortaleza: {
    nombre: string;
    partidas: number;
    promedioPuntos: number;
    porcentajeAcierto: number;
  } | null;
  debilidad: {
    nombre: string;
    partidas: number;
    promedioPuntos: number;
    porcentajeAcierto: number;
  } | null;
  categoriasResumen: Array<{
    nombre: string;
    partidas: number;
    promedioPuntos: number;
    porcentajeAcierto: number;
  }>;
  comparativaGlobal: {
    promedioPuntosUsuario: number;
    promedioPuntosGlobal: number;
    diferenciaPuntos: number;
    porcentajeAciertoUsuario: number;
    porcentajeAciertoGlobal: number;
    diferenciaAcierto: number;
  };
  ultimas10: StatRow[];
  tendenciaUltimasPartidas: 'subiendo' | 'bajando' | 'estable';
  recordClassic: {
    puntos: number;
    aciertos: number;
    dificultad: 'normal' | 'dificil';
    fechaPartida: string;
  } | null;
  recordNoHit: {
    racha: number;
    puntos: number;
    dificultad: 'normal' | 'dificil';
    fechaPartida: string;
  } | null;
  topClassic: StatRow[];
  topNoHit: StatRow[];
}

interface UserStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserStatsModal: React.FC<UserStatsModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<UserStatsResponse | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(getApiUrl('/api/ranking/mis-estadisticas'), {
          withCredentials: true,
        });
        setStats(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'No se pudieron cargar tus estadísticas');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const maxWeeklyPoints = Math.max(...(stats?.weeklyEvolution.map((w) => w.puntosTotales) || [1]));
  const maxDailyActivity = Math.max(...(stats?.activityCalendar.map((d) => d.partidas) || [1]));

  const getHeatColor = (partidas: number) => {
    if (partidas <= 0) return 'bg-white/10';
    const ratio = partidas / maxDailyActivity;
    if (ratio < 0.34) return 'bg-cyan-700/70';
    if (ratio < 0.67) return 'bg-cyan-500/80';
    return 'bg-cyan-300';
  };

  const renderTopRows = (rows: StatRow[], modalidad: 'classic' | 'nohit') => {
    if (rows.length === 0) {
      return <p className="text-white/50 text-sm font-main">Aún no hay partidas en esta modalidad.</p>;
    }

    return (
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {rows.map((row, index) => (
          <div key={row._id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
            <div className="min-w-0">
              <p className="font-main text-white text-sm">
                #{index + 1} · {row.dificultad === 'dificil' ? 'Difícil' : 'Normal'}
              </p>
              <p className="font-main text-white/50 text-xs">
                {new Date(row.fechaPartida).toLocaleDateString('es-ES')}
              </p>
            </div>
            <div className="text-right shrink-0">
              {modalidad === 'classic' ? (
                <p className="font-gaming text-gaming-gold text-xs">{row.puntos} pts</p>
              ) : (
                <p className="font-gaming text-gaming-neon-orange text-xs">Racha {row.aciertos}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLastRows = (rows: StatRow[]) => {
    if (rows.length === 0) {
      return <p className="text-white/50 text-sm font-main">Aún no hay partidas recientes.</p>;
    }

    return (
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {rows.map((row, index) => (
          <div key={`${row._id}-${index}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
            <div className="min-w-0">
              <p className="font-main text-white text-sm capitalize">
                {row.modalidad} · {row.dificultad === 'dificil' ? 'Difícil' : 'Normal'}
              </p>
              <p className="font-main text-white/50 text-xs">
                {new Date(row.fechaPartida).toLocaleString('es-ES')}
              </p>
            </div>
            <div className="text-right shrink-0">
              {row.modalidad === 'classic' ? (
                <p className="font-gaming text-gaming-gold text-xs">{row.puntos} pts</p>
              ) : (
                <p className="font-gaming text-gaming-neon-orange text-xs">Racha {row.aciertos}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-[81px] bg-black/80 flex items-start justify-center pt-6 z-40" onClick={onClose}>
      <div className="card-gaming max-w-3xl w-full mx-4 max-h-[calc(100vh-120px)] overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-gaming text-gaming-cyan text-lg tracking-wider">ESTADÍSTICAS</h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors text-2xl"
            aria-label="Cerrar estadísticas"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 font-main text-center py-8">Cargando estadísticas...</div>
        ) : error ? (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded text-sm font-main">
            {error}
          </div>
        ) : stats ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="font-main text-white/60 text-xs mb-1">Jugador</p>
                <p className="font-main text-white font-bold truncate">{stats.nickname}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="font-main text-white/60 text-xs mb-1">Porcentaje de acierto medio</p>
                <p className="font-gaming text-gaming-gold text-base">{stats.porcentajeAciertoMedio}%</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="font-main text-white/60 text-xs mb-1">Partidas totales</p>
                <p className="font-gaming text-gaming-purple text-base">{stats.totalPartidas}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="font-main text-white/60 text-xs mb-1">Racha de días</p>
                <p className="font-gaming text-gaming-neon-orange text-base">{stats.rachaDiasActual} días</p>
                <p className="font-main text-white/40 text-xs mt-1">
                  Mejor: {stats.mejorRachaDias} · 24h: {stats.participoUltimas24h ? 'Activo' : 'Inactivo'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">RACHAS NO HIT</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="font-main text-white/60 text-xs">Racha actual</p>
                    <p className="font-gaming text-gaming-neon-orange text-base">{stats.rachaActualNoHit}</p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="font-main text-white/60 text-xs">Mejor racha</p>
                    <p className="font-gaming text-gaming-neon-orange text-base">{stats.mejorRachaNoHit}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">COMPARATIVA GLOBAL</h3>
                <p className="font-main text-white/70 text-sm">
                  Puntos medios: <span className="text-white">{stats.comparativaGlobal.promedioPuntosUsuario}</span> vs global <span className="text-white">{stats.comparativaGlobal.promedioPuntosGlobal}</span>
                </p>
                <p className="font-main text-white/70 text-sm mt-1">
                  Acierto: <span className="text-white">{stats.comparativaGlobal.porcentajeAciertoUsuario}%</span> vs global <span className="text-white">{stats.comparativaGlobal.porcentajeAciertoGlobal}%</span>
                </p>
                <p className={`font-main text-sm mt-2 ${stats.comparativaGlobal.diferenciaAcierto >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.comparativaGlobal.diferenciaAcierto >= 0 ? '+' : ''}
                  {stats.comparativaGlobal.diferenciaAcierto}% de diferencia en acierto
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">ACTIVIDAD ÚLTIMOS 35 DÍAS</h3>
              <div className="grid grid-cols-7 gap-2">
                {stats.activityCalendar.map((day) => (
                  <div
                    key={day.date}
                    className={`h-8 rounded-md border border-white/10 ${getHeatColor(day.partidas)} flex items-center justify-center text-[10px] font-main text-white/80`}
                    title={`${day.date} · ${day.partidas} partida${day.partidas === 1 ? '' : 's'}`}
                  >
                    {day.date.slice(8, 10)}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 text-[11px] text-white/50 font-main">
                <span>Menor actividad</span>
                <span>Mayor actividad</span>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">EVOLUCIÓN SEMANAL (8 semanas)</h3>
              <div className="space-y-2">
                {stats.weeklyEvolution.map((week) => {
                  const width = Math.max(6, Math.round((week.puntosTotales / maxWeeklyPoints) * 100));
                  return (
                    <div key={week.label}>
                      <div className="flex items-center justify-between text-xs font-main text-white/60 mb-1">
                        <span>{week.label}</span>
                        <span>{week.puntosTotales} pts · {week.porcentajeAcierto}% · {week.partidas} partidas</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gaming-cyan rounded-full" style={{ width: `${width}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">RENDIMIENTO POR DIFICULTAD</h3>
                <div className="space-y-2">
                  {stats.dificultadStats.map((d) => (
                    <div key={d.dificultad} className="rounded-lg border border-white/10 bg-white/5 p-3">
                      <p className="font-main text-white text-sm">{d.dificultad === 'dificil' ? 'Difícil' : 'Normal'}</p>
                      <p className="font-main text-white/60 text-xs">{d.partidas} partidas · {d.promedioPuntos} pts media · {d.porcentajeAcierto}% acierto</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">FORTALEZAS Y DEBILIDADES</h3>
                <div className="space-y-2">
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                    <p className="font-main text-emerald-300 text-sm">Fortaleza</p>
                    <p className="font-main text-white text-sm">{stats.fortaleza?.nombre || 'Sin datos'}</p>
                    {stats.fortaleza && (
                      <p className="font-main text-white/60 text-xs">{stats.fortaleza.porcentajeAcierto}% acierto · {stats.fortaleza.promedioPuntos} pts media</p>
                    )}
                  </div>
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
                    <p className="font-main text-rose-300 text-sm">Debilidad</p>
                    <p className="font-main text-white text-sm">{stats.debilidad?.nombre || 'Sin datos'}</p>
                    {stats.debilidad && (
                      <p className="font-main text-white/60 text-xs">{stats.debilidad.porcentajeAcierto}% acierto · {stats.debilidad.promedioPuntos} pts media</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">RÉCORD PERSONAL CLASSIC</h3>
                {stats.recordClassic ? (
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="font-main text-white text-sm">{stats.recordClassic.puntos} pts · {stats.recordClassic.aciertos} aciertos</p>
                    <p className="font-main text-white/60 text-xs">{stats.recordClassic.dificultad === 'dificil' ? 'Difícil' : 'Normal'} · {new Date(stats.recordClassic.fechaPartida).toLocaleDateString('es-ES')}</p>
                  </div>
                ) : (
                  <p className="text-white/50 text-sm font-main">Sin partidas classic todavía.</p>
                )}
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider mb-3">RÉCORD PERSONAL NO HIT</h3>
                {stats.recordNoHit ? (
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="font-main text-white text-sm">Racha {stats.recordNoHit.racha} · {stats.recordNoHit.puntos} pts</p>
                    <p className="font-main text-white/60 text-xs">{stats.recordNoHit.dificultad === 'dificil' ? 'Difícil' : 'Normal'} · {new Date(stats.recordNoHit.fechaPartida).toLocaleDateString('es-ES')}</p>
                  </div>
                ) : (
                  <p className="text-white/50 text-sm font-main">Sin partidas no hit todavía.</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-gaming text-gaming-cyan text-sm tracking-wider">ÚLTIMAS 10 PARTIDAS</h3>
                <span className={`font-main text-xs px-2 py-1 rounded-full border ${
                  stats.tendenciaUltimasPartidas === 'subiendo'
                    ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                    : stats.tendenciaUltimasPartidas === 'bajando'
                      ? 'text-rose-300 border-rose-500/40 bg-rose-500/10'
                      : 'text-white/70 border-white/20 bg-white/5'
                }`}>
                  Tendencia: {stats.tendenciaUltimasPartidas}
                </span>
              </div>
              {renderLastRows(stats.ultimas10)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-purple text-sm tracking-wider mb-3">TOP 10 CLASSIC</h3>
                {renderTopRows(stats.topClassic, 'classic')}
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <h3 className="font-gaming text-gaming-neon-orange text-sm tracking-wider mb-3">TOP 10 NO HIT</h3>
                {renderTopRows(stats.topNoHit, 'nohit')}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UserStatsModal;
